import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react';
import translations from '../translations/translations.json';
import {
  createTranslator,
  setGlobalTranslator,
  Language as LanguageType,
  AllTranslations as AllTranslationsType,
  TranslationValue as TranslationValueType,
  LanguageTranslations as LanguageTranslationsType,
} from '../utils/translations';

type TranslationValue = TranslationValueType;

// Represents a single language option for a specific product
interface ProductLanguageItem {
  productName: string;
  drupalCode: string; // Used in CDN translation file URLs (e.g., 'en', 'fr', 'de')
  languageLocaleCode: string; // Full locale code (e.g., 'en-US', 'fr-FR')
  name: string; // Display name in English
  localizedName: string; // Display name in the native language
  isAvailableForPI2: boolean;
}

// Maps product names to their available languages
// Used to look up the correct drupalCode for CDN translation URLs
interface ProductLanguageMap {
  BehavioralAssessment: ProductLanguageItem[];
  GroupAnalytics: ProductLanguageItem[];
  Cognitive: ProductLanguageItem[];
  CognitiveJA: ProductLanguageItem[];
  FullSite: ProductLanguageItem[];
  Guides: ProductLanguageItem[];
  PDC: ProductLanguageItem[];
  BehavioralReports: ProductLanguageItem[];
  Placard: ProductLanguageItem[];
  JobAssessment: ProductLanguageItem[];
  JobReports: ProductLanguageItem[];
  CognitiveReports: ProductLanguageItem[];
  GenderNeutralReports: ProductLanguageItem[];
  DoNotConsiderAvailability: ProductLanguageItem[];
}

// Response structure from the initData endpoint
interface InitDataResponse {
  clientConfig: { translationVersion: string; [key: string]: unknown };
  language: { display: string; locale: string; drupalCode: string };
  productLanguageMap?: ProductLanguageMap;
}

// Result from fetching translation version and drupal code
interface TranslationVersionResult {
  translationVersion: string | null;
  drupalCode: string | null; // Used to construct CDN translation file URLs
}

type LanguageTranslations = LanguageTranslationsType;
type AllTranslations = AllTranslationsType;

export type Language = LanguageType;

// PI2 supported language locale codes (BCP 47 format)
export type LanguageLocale =
  | 'da-DK' // Danish
  | 'de-DE' // German
  | 'en-US' // English (US)
  | 'es-ES' // Spanish
  | 'fr-FR' // French
  | 'it-IT' // Italian
  | 'ko-KR' // Korean
  | 'nl-NL' // Dutch
  | 'pl-PL' // Polish
  | 'pt-BR' // Portuguese (Brazil)
  | 'ru-RU' // Russian
  | 'sv-SE' // Swedish
  | 'zh-CN'; // Chinese (Simplified)

interface TranslationsContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  tr: (key: string, ...params: string[]) => string;
  getValue: (key: string) => string | undefined;
}

const TranslationsContext = createContext<TranslationsContextType | undefined>(undefined);

interface TranslationsProviderProps {
  children: ReactNode;
  languageLocale?: LanguageLocale; // e.g. 'en-US', 'fr-FR', 'de-DE', etc. Default: 'en-US'
  translationUrl?: string;
  initDataUrl?: string;
}

/**
 * Helper function to find the drupalCode by languageLocale
 * The drupalCode is used to construct the CDN translation file URL
 * @param languageLocale - Full locale code (e.g., 'en-US', 'fr-FR')
 * @returns drupalCode (e.g., 'en', 'fr') or null if not found
 */
const findDrupalCodeByLocale = (languageLocale: LanguageLocale): string => {
  // These are all the supported PI2 languages and their drupal codes
  // See the PI language table
  // For now we don't need to map every possible combination
  const mapping: Record<LanguageLocale, string> = {
    'da-DK': 'da',
    'de-DE': 'de',
    'en-US': 'en',
    'es-ES': 'es',
    'fr-FR': 'fr',
    'it-IT': 'it',
    'ko-KR': 'ko',
    'nl-NL': 'nl',
    'pl-PL': 'pl',
    'pt-BR': 'pt-br',
    'ru-RU': 'ru',
    'sv-SE': 'sv',
    'zh-CN': 'zh-hans',
  };

  return mapping[languageLocale] || 'en';
};

/**
 * Fetches translation version and drupalCode from the initData endpoint
 * @param initDataUrl - URL to fetch initData from (contains version and language mapping)
 * @param languageLocale - Full locale code (e.g., 'en-US') to look up drupalCode
 * @returns Object containing translationVersion and drupalCode, or nulls if failed
 */
const fetchTranslationVersion = async (
  languageLocale: LanguageLocale,
  initDataUrl?: string
): Promise<TranslationVersionResult> => {
  if (!initDataUrl) {
    console.warn('No initDataUrl provided, cannot fetch translation version');
    return { translationVersion: null, drupalCode: null };
  }

  try {
    const res = await fetch(initDataUrl);
    if (!res.ok) return { translationVersion: null, drupalCode: null };
    const data = (await res.json()) as InitDataResponse;

    const translationVersion = data?.clientConfig?.translationVersion ?? null;
    if (!translationVersion) return { translationVersion: null, drupalCode: null };

    const drupalCode = findDrupalCodeByLocale(languageLocale);
    if (!drupalCode) {
      console.warn('Could not determine drupalCode for language locale:', languageLocale);
      return { translationVersion, drupalCode: null };
    }

    return { translationVersion, drupalCode };
  } catch (e) {
    console.error('Failed to fetch translationVersion', e);
    return { translationVersion: null, drupalCode: null };
  }
};

/**
 * Parses translation file content from the CDN
 * CDN files are wrapped in legacy JS format: var Translations={...};
 * This function strips the prefix and parses the JSON content
 * @param content - Raw content from CDN translation file
 * @returns Parsed translation object
 */
const parseTranslationFileContent = (content: string): { [key: string]: string } => {
  const prefix = 'var Translations=';
  const trimmed = content.trim();
  if (trimmed.startsWith(prefix)) {
    return JSON.parse(trimmed.slice(prefix.length));
  }
  return JSON.parse(trimmed);
};

/**
 * Normalizes flat CDN translation object to match local LanguageTranslations structure
 * CDN format is flat: { "key": "value" }
 * Local format has metadata: { "key": { value: "value", description?: string, ... } }
 * @param remote - Flat translation object from CDN
 * @returns Normalized translation object matching LanguageTranslations interface
 */
const normalizeCdnTranslations = (remote: { [key: string]: string }): LanguageTranslations =>
  Object.entries(remote).reduce<LanguageTranslations>((acc, [key, value]) => {
    // Accept either flat string values or objects matching TranslationValue
    if (typeof value === 'string') {
      acc[key] = { value };
      return acc;
    }

    // If it's an object with a string `value` property, accept it
    const unknownValue: unknown = value;
    if (unknownValue && typeof unknownValue === 'object') {
      const obj = unknownValue as { value?: unknown };
      if (typeof obj.value === 'string') {
        acc[key] = obj as TranslationValue;
        return acc;
      }
    }

    // Unexpected format — log, but fall back to using the key as the value
    // This ensures the UI shows a sensible placeholder while still surfacing the issue
    console.error('Invalid translation entry for key', key, ': expected string or TranslationValue, got', typeof value);
    acc[key] = { value: key };
    return acc;
  }, {});

/**
 * Fetches translations from CDN and merges them with local translations
 * @param language - Language code (e.g., 'en', 'fr', 'de') for internal state management
 * @param translationUrl - Base URL for CDN translations
 * @param initDataUrl - URL for fetching translation version and language mapping
 * @param languageLocale - Full locale code (e.g., 'en-US', 'fr-FR') for drupalCode lookup
 * @returns Merged translations object with English fallback
 */
const fetchAndMergeTranslations = async (
  languageLocale: LanguageLocale,
  language: string,
  translationUrl: string,
  initDataUrl: string
) => {
  if (!translationUrl) {
    console.warn('No translationUrl provided, using local translations only');
    return { en: translations.en };
  }

  const result = await fetchTranslationVersion(languageLocale, initDataUrl);
  if (!result?.translationVersion || !result?.drupalCode) {
    console.error('Could not get translation version and drupal code');
    return { en: translations.en };
  }

  const { translationVersion, drupalCode } = result;
  const url = `${translationUrl}/translations.${drupalCode}.json?v=${translationVersion}`;

  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`Could not fetch translations for ${language} (drupalCode: ${drupalCode}), falling back to English`);
      return { en: translations.en };
    }

    const text = await res.text();
    const remote = parseTranslationFileContent(text);
    const normalized = normalizeCdnTranslations(remote);

    const merged = { ...translations.en, ...normalized };
    return { en: translations.en, [language]: merged };
  } catch (e) {
    console.error('Failed to fetch/merge translations', e);
    return { en: translations.en };
  }
};

export const TranslationsProvider = ({
  children,
  languageLocale = 'en-US',
  translationUrl = 'https://translations.predictiveindex.com',
  initDataUrl = 'https://api.predictiveindex.com/initdata/api/v1/initdata',
}: TranslationsProviderProps) => {
  const languageCode = (languageLocale.split('-')[0] ?? 'en') as Language;
  const [language, setLanguage] = useState<Language>(languageCode);
  const [typedTranslations, setTypedTranslations] = useState<AllTranslations>(translations as AllTranslations);

  useEffect(() => {
    let isMounted = true;
    // show local translations immediately to avoid flash
    setTypedTranslations(translations as AllTranslations);

    const load = async () => {
      try {
        const merged = await fetchAndMergeTranslations(languageLocale, language, translationUrl, initDataUrl);
        if (isMounted && merged) setTypedTranslations(merged as AllTranslations);
      } catch (e) {
        console.error('Failed to load remote translations', e);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [language, translationUrl, initDataUrl, languageLocale]);

  const translator = useMemo(() => createTranslator(language, typedTranslations), [language, typedTranslations]);

  useEffect(() => {
    setGlobalTranslator(translator);
  }, [translator]);

  const tr = translator.tr;
  const getValue = translator.getValue;

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      tr,
      getValue,
    }),
    [language, setLanguage, tr, getValue]
  );

  return <TranslationsContext.Provider value={value}>{children}</TranslationsContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(TranslationsContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationsProvider');
  }
  return context;
};
